import type { BlogPostSummary, SitePageSummary, VisitorConfig } from '../../types';
import { SiteHeader } from './PageRenderer';
import { SiteFooter } from './SiteFooter';
import { SiteWidgets } from './SiteWidgets';
import { useDocumentMeta } from '../../utils/useDocumentMeta';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

interface BlogIndexProps {
  config: VisitorConfig;
  posts: BlogPostSummary[];
  menuPages: SitePageSummary[];
}

/** Liste des articles publiés, du plus récent au plus ancien — voir /blog. */
export function BlogIndex({ config, posts, menuPages }: BlogIndexProps) {
  useDocumentMeta(
    `Blog — ${config.brandLogoText}`,
    `Actualités et conseils de ${config.brandLogoText}.`
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SiteHeader config={config} menuPages={menuPages} />

      <main className="max-w-5xl mx-auto px-4 py-12 flex-1 w-full">
        <h1 className="text-3xl font-black mb-8" style={{ color: config.brandColor }}>
          Blog
        </h1>

        {posts.length === 0 ? (
          <p className="text-gray-400 text-sm py-16 text-center">
            Aucun article publié pour l'instant.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <a key={post.id} href={`/blog/${post.slug}`} className="group block">
                <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden mb-3">
                  {post.coverImageUrl && (
                    <img
                      src={post.coverImageUrl}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  )}
                </div>
                <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide mb-1">
                  {formatDate(post.publishedAt)}
                  {post.authorName && ` — ${post.authorName}`}
                </p>
                <h2 className="text-lg font-bold leading-snug group-hover:opacity-70 transition-opacity">
                  {post.title}
                </h2>
                {post.excerpt && (
                  <p className="text-sm text-gray-500 mt-1.5 line-clamp-3">{post.excerpt}</p>
                )}
              </a>
            ))}
          </div>
        )}
      </main>

      <SiteFooter config={config} menuPages={menuPages} />
      <SiteWidgets config={config} />
    </div>
  );
}
